<template>
    <auth-layout>
        <auth-card-header title="Welcome, sign in to continue" />

        <validation-observer v-slot="{ handleSubmit }" slim>
            <form class="auth-form" @submit.prevent="handleSubmit(signIn)">
                <validation-provider v-slot="{ errors, failed }" rules="required" name="Username or email">
                    <c-input
                        v-model="username"
                        label="Username or Email"
                        placeholder="Type your username"
                        :error="failed"
                        :error-msg="errors[0]"
                    />
                </validation-provider>

                <validation-provider v-slot="{ errors, failed }" rules="required" name="Password">
                    <c-input
                        v-model="password"
                        class="mb-2"
                        type="password"
                        label="Password"
                        placeholder="Type your password"
                        :error="failed"
                        :error-msg="errors[0]"
                    />
                    <a class="forgot-password" href="/forgotPassword">Forgot your password?</a>
                </validation-provider>

                <c-error v-if="errorMessage" :message="errorMessage" />

                <c-button
                    type="submit"
                    variant="primary"
                    :disabled="signingIn || !!loadingProvider"
                    :text="signingIn ? 'Signing in...' : 'Log In'"
                />
                <p class="signup-link">Don't have an account? <a href="/signup">Create one</a></p>
            </form>
        </validation-observer>

        <div class="auth-providers">
            <or-divider />
            <div class="auth-providers-buttons">
                <social-auth-button
                    provider="google"
                    label="Log In with Google"
                    :loading="loadingProvider === 'google'"
                    :disabled="!!loadingProvider || signingIn"
                    @click="signInWith('google')"
                />
                <social-auth-button
                    provider="etsy"
                    label="Log In with Etsy"
                    :loading="loadingProvider === 'etsy'"
                    :disabled="!!loadingProvider || signingIn"
                    @click="signInWith('etsy')"
                />
            </div>
        </div>
    </auth-layout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { AxiosError } from 'axios';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import AuthLayout from '@/components/Auth/AuthLayout.vue';
import AuthCardHeader from '@/components/Auth/AuthCardHeader.vue';
import OrDivider from '@/components/Auth/OrDivider.vue';
import SocialAuthButton from '@/components/Auth/SocialAuthButton.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import CError from '@/components/shared/CError.vue';
import CInput from '@/components/shared/CInput/CInput.vue';
import { externalLogin, passwordLogin } from '@/api/auth';
import { useExternalAuth } from '@/composables/externalAuth';
import { externalAuthErrorMessage, isExpectedAuthError } from '@/utils/authUtils';
import { notifyAngularSignIn } from '@/utils/userUtils';
import { ExternalAuthOutcome, type ExternalProvider } from '@/typings/auth/externalAuth';

// The backend's wording for a wrong username or password
const INVALID_CREDENTIALS = 'The email or password you entered is incorrect';

const { authenticate, loadingProvider } = useExternalAuth();

const username = ref('');
const password = ref('');
const signingIn = ref(false);
const errorMessage = ref('');

const signIn = async () => {
    errorMessage.value = '';

    signingIn.value = true;
    try {
        await passwordLogin(username.value.trim(), password.value);
        notifyAngularSignIn();
    } catch (error) {
        // Wrong credentials are expected, anything else is worth a look (same rule as the AngularJS page had)
        if ((error as AxiosError)?.response?.data?.error !== INVALID_CREDENTIALS) {
            window.rumReportError(error as Error);
        }
        errorMessage.value = externalAuthErrorMessage(error, INVALID_CREDENTIALS);
    } finally {
        signingIn.value = false;
    }
};

const signInWith = async (provider: ExternalProvider) => {
    errorMessage.value = '';
    try {
        await authenticate(provider, 'signin', {}, async (result) => {
            if (result.outcome === ExternalAuthOutcome.CheckoutRequired && result.checkoutUrl) {
                // Signed up but never paid: back to the checkout before the app opens
                window.location.assign(result.checkoutUrl);
                return 'navigating';
            }

            if (result.loginTicket) {
                await externalLogin(result.loginTicket);
                notifyAngularSignIn();
            }
        });
    } catch (error) {
        // A closed popup or a code the user can act on is not a failure to report
        if ((error as AxiosError)?.isAxiosError && !isExpectedAuthError(error)) {
            window.rumReportError(error as Error);
        }
        errorMessage.value = externalAuthErrorMessage(error, 'We could not sign you in. Please try again.');
    }
};
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.auth-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.forgot-password {
    font-size: 0.875rem;
    font-style: italic;
}

.signup-link {
    margin: 0;
    font-size: 0.75rem;
    text-align: center;
}

.auth-providers {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    .auth-providers-buttons {
        display: flex;
        gap: 1rem;
    }
}

.auth-providers-buttons > .social-auth-button {
    flex: 1 1 0;
    min-width: 0;
}

@media (max-width: 575px) {
    .auth-providers-buttons {
        flex-direction: column;
    }
}
</style>
